import React, { useRef, useState } from "react";
import { RecoveryPhraseSettings } from "../components/RecoveryPhraseSettings";
import { X, Loader2, Camera, ScrollText, Lightbulb } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Avatar } from "../components/Media";
import { useAuth } from "../context/AuthContext";
import { MfaSettings } from "../components/MfaSetup";
import { useCrypto } from "../context/CryptoContext";
import { DomainPicker } from "../components/DomainPicker";
import { CreedViewer } from "../components/SovereignCreed";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase">{label}</span>
    <input {...props} className="mt-2 bg-[#0A0A0A] border border-white/15 rounded-none px-3 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] w-full font-body" />
  </label>
);
const Area = ({ label, ...props }) => (
  <label className="block">
    <span className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase">{label}</span>
    <textarea {...props} rows={3} className="mt-2 bg-[#0A0A0A] border border-white/15 rounded-none px-3 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] w-full font-body resize-none" />
  </label>
);

export default function EditProfile({ profile, onClose, onSaved, decrypted = {} }) {
  const { user, setUser, resetTips } = useAuth();
  const [showCreed, setShowCreed] = useState(false);
  const [tipsReset, setTipsReset] = useState(false);
  const { hasNetwork, encryptNetText, encryptNetBytes } = useCrypto();
  const dv = (f) => {
    const d = decrypted[f];
    if (d && d !== "__LOCKED__") return d;
    return profile[f] || "";
  };
  const [form, setForm] = useState({
    name: profile.name || "",
    domains: profile.domains?.length ? profile.domains : (profile.contribution_type ? [profile.contribution_type] : []),
    headline: profile.headline || "", bio: dv("bio"),
    offering: dv("offering"), seeking: dv("seeking"), link: dv("link"),
    instagram: dv("instagram"),
  });
  const [avatar, setAvatar] = useState(profile.avatar);
  const [avatarIv, setAvatarIv] = useState(profile.avatar_iv || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef();
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Downscale a portrait to a sane size (square-ish, max 512px) before encrypting.
  const shrinkToJpeg = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const max = 512;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      c.toBlob((b) => b ? resolve(b) : reject(new Error("encode failed")), "image/jpeg", 0.85);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

  const handleAvatar = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true); setError("");
    try {
      if (hasNetwork()) {
        const blob = await shrinkToJpeg(f).catch(() => f);
        const buf = await blob.arrayBuffer();
        const { ctBuf, ivB64 } = await encryptNetBytes(buf);
        const fd = new FormData();
        fd.append("file", new Blob([ctBuf]), "enc.bin");
        fd.append("enc", "true");
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAvatar(data.path); setAvatarIv(ivB64);
      } else {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAvatar(data.path); setAvatarIv(null);
      }
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.domains.length) { setError("Choose at least one realm."); return; }
    const ig = (form.instagram || "").replace(/^@+/, "").replace(/^https?:\/\/(www\.)?instagram\.com\//i, "").replace(/\/+$/, "").trim();
    // Normalize the link so it always stores clean & clickable: prepend https:// if missing.
    let link = (form.link || "").trim();
    if (link) {
      if (!/^https?:\/\//i.test(link)) link = `https://${link}`;
      if (!/^https?:\/\/[^\s.]+\.[^\s]{2,}$/i.test(link)) {
        setError("That link doesn't look valid, include a domain like example.com");
        return;
      }
    }
    const f2 = { ...form, instagram: ig, link };
    setSaving(true); setError("");
    try {
      let payload;
      if (hasNetwork()) {
        const enc_fields = {};
        for (const f of ["name", "headline", "bio", "offering", "seeking", "link", "instagram"]) {
          const e = await encryptNetText((f2[f] || "").trim());
          enc_fields[f] = { ct: e.ct, iv: e.iv };
        }
        payload = { domains: f2.domains, avatar, avatar_iv: avatarIv, enc_fields };
      } else {
        payload = { name: f2.name, headline: f2.headline, domains: f2.domains, avatar,
                    bio: f2.bio, offering: f2.offering, seeking: f2.seeking, link: f2.link, instagram: ig };
      }
      const { data } = await api.patch("/me", payload);
      setUser(data);
      onSaved && onSaved(data);
      onClose();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="edit-profile-modal">
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-heading text-lg text-white">Edit your signal</h3>
          <button onClick={onClose} data-testid="close-edit-button" className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex items-center gap-4">
            <button onClick={() => inputRef.current.click()} data-testid="avatar-upload-button" className="relative group">
              <Avatar name={form.name} src={avatar} iv={avatarIv} size={72} />
              <span className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                {uploading ? <Loader2 className="animate-spin text-white" size={18} /> : <Camera size={18} className="text-white" />}
              </span>
            </button>
            <span className="font-body text-xs text-zinc-500">Tap to change your portrait</span>
            <input ref={inputRef} type="file" accept="image/*" onChange={handleAvatar} className="hidden" data-testid="avatar-file-input" />
          </div>
          <Field label="Name" value={form.name} onChange={upd("name")} data-testid="edit-name-input" />
          <label className="block">
            <span className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase">Realms (up to 5)</span>
            <div className="mt-2" data-testid="edit-category-select">
              <DomainPicker value={form.domains} onChange={(d) => setForm({ ...form, domains: d })} />
            </div>
          </label>
          <Field label="Headline" value={form.headline} onChange={upd("headline")} data-testid="edit-headline-input" />
          <Area label="Story" value={form.bio} onChange={upd("bio")} data-testid="edit-bio-input" />
          <Area label="Offers" value={form.offering} onChange={upd("offering")} data-testid="edit-offering-input" />
          <Area label="Seeks" value={form.seeking} onChange={upd("seeking")} data-testid="edit-seeking-input" />
          <Field label="Link" value={form.link} onChange={upd("link")} data-testid="edit-link-input" />
          <label className="block">
            <span className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase">Instagram <span className="text-zinc-600 normal-case tracking-normal">(optional)</span></span>
            <div className="mt-2 flex items-center bg-[#0A0A0A] border border-white/15 focus-within:border-[#14A89A]">
              <span className="pl-3 pr-1 text-zinc-500 font-body select-none">@</span>
              <input
                value={form.instagram}
                onChange={(e) => setForm({ ...form, instagram: e.target.value.replace(/^@+/, "") })}
                onBlur={(e) => setForm({ ...form, instagram: e.target.value.replace(/^@+/, "").replace(/^https?:\/\/(www\.)?instagram\.com\//i, "").replace(/\/+$/, "").trim() })}
                placeholder="yourhandle"
                data-testid="edit-instagram-input"
                className="flex-1 bg-transparent py-3 pr-3 text-white placeholder:text-zinc-600 focus:outline-none font-body"
              />
            </div>
          </label>
          {error && <p className="text-red-400 text-sm font-body" data-testid="edit-error">{error}</p>}
          <button onClick={save} disabled={saving || uploading} data-testid="save-profile-button" className="w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-3 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
            {saving ? <Loader2 className="animate-spin" size={18} /> : "Save"}
          </button>
          <button onClick={() => setShowCreed(true)} data-testid="view-creed-link" className="w-full flex items-center justify-center gap-2 text-zinc-500 hover:text-[#D4AF37] transition-colors font-body text-xs pt-1">
            <ScrollText size={13} /> View the Sovereign Creed
          </button>
          <button onClick={async () => { await resetTips(); setTipsReset(true); }} data-testid="replay-tips-link" className="w-full flex items-center justify-center gap-2 text-zinc-500 hover:text-[#14A89A] transition-colors font-body text-xs">
            <Lightbulb size={13} /> {tipsReset ? "Tips reset: they'll reappear as you explore" : "Replay the guided tips"}
          </button>
          <div className="pt-2"><MfaSettings /></div>
          <RecoveryPhraseSettings />
        </div>
      </div>
      {showCreed && <CreedViewer onClose={() => setShowCreed(false)} acceptedAt={user?.creed_accepted_at} version={user?.creed_version} />}
    </div>
  );
}
